import { DollarSign, TrendingUp, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { StatCard } from "@/components/analytics/StatCard";
import { CostChart } from "@/components/analytics/CostChart";
import { ScanHistoryTable } from "@/components/analytics/ScanHistoryTable";

export function CostTrackingPage() {
  const { data: monthlyCosts, isLoading: monthlyLoading } =
    trpc.analytics.monthlyCosts.useQuery({ months: 12 });
  const { data: costBreakdown, isLoading: breakdownLoading } =
    trpc.analytics.costBreakdown.useQuery({});
  const { data: history } = trpc.analytics.scanHistory.useQuery({ limit: 50 });

  const isLoading = monthlyLoading || breakdownLoading;

  const totalSpend = costBreakdown?.totalCost ?? 0;

  const thisMonthCost =
    monthlyCosts?.months && monthlyCosts.months.length > 0
      ? (monthlyCosts.months.at(-1)?.totalCost ?? 0)
      : 0;

  const completedScans = history?.filter((h) => h.status === "completed") ?? [];
  const avgPerScan =
    completedScans.length > 0 ? totalSpend / completedScans.length : 0;

  // Build cost breakdown bars
  const breakdownItems = costBreakdown?.breakdown ?? [];
  const maxBreakdownCost = Math.max(
    ...breakdownItems.map((b) => b.cost),
    0.01
  );

  const sourceColors: Record<string, string> = {
    google_places_api: "bg-emerald-500",
  };

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <h1 className="text-2xl font-semibold text-foreground">Cost Tracking</h1>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          Loading cost data...
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              icon={DollarSign}
              label="Total Spend"
              value={`$${totalSpend.toFixed(2)}`}
              subtext="All-time"
            />
            <StatCard
              icon={TrendingUp}
              label="This Month"
              value={`$${thisMonthCost.toFixed(2)}`}
            />
            <StatCard
              icon={Activity}
              label="Avg per Scan"
              value={`$${avgPerScan.toFixed(2)}`}
              subtext={`${completedScans.length} completed scans`}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Costs</CardTitle>
            </CardHeader>
            <CardContent>
              <CostChart
                data={(monthlyCosts?.months ?? []).map((m) => ({
                  month: m.month ?? "",
                  totalCost: m.totalCost,
                  scanCount: m.scanCount,
                }))}
              />
            </CardContent>
          </Card>

          {breakdownItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {breakdownItems.map((item) => {
                    const pct = (item.cost / maxBreakdownCost) * 100;
                    const colorClass =
                      sourceColors[item.source] ?? "bg-gray-400";
                    return (
                      <div key={item.source} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="capitalize">{item.source.replace(/_/g, " ")}</span>
                          <span className="text-muted-foreground">
                            ${item.cost.toFixed(2)} ({item.count}{" "}
                            {item.count === 1 ? "scan" : "scans"})
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-muted">
                          <div
                            className={`h-full rounded-full ${colorClass}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Per-Scan Cost History</CardTitle>
            </CardHeader>
            <CardContent>
              <ScanHistoryTable data={history ?? []} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
